import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { EDITABLE_CATEGORY_PROPERTY } from 'app/constants/AdminBrowseConstants';
import {
  addEmptyCategory,
  deleteCategoryFromConfig,
  getSubCategories,
  isUnsavedCategory,
  moveCategoryPosition,
  toggleCategoryIsEnabled,
  updateCategoryProperty
} from 'app/helpers/browse/admin';
import { categoryConfigShape } from 'app/shapes/browse';
import CategoryListEditor from './CategoryListEditor';

const DEBOUNCE_DURATION_MS = 250;

const CategoryConfigEditor = ({ categoryConfig, save, topLevelCategoryId }) => {
  const [categoryConfigWorkingCopy, setCategoryConfigWorkingCopy] = useState(cloneDeep(categoryConfig));
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setIsDirty(!isEqual(categoryConfig, categoryConfigWorkingCopy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryConfigWorkingCopy]);

  const addCategory = parentCategoryId => {
    setCategoryConfigWorkingCopy(configWorkingCopy => addEmptyCategory(parentCategoryId, configWorkingCopy));
  };

  const deleteCategory = subCategoryId => {
    setCategoryConfigWorkingCopy(configWorkingCopy => deleteCategoryFromConfig(subCategoryId, configWorkingCopy));
  };

  const moveCategory = (subCategoryId, moveCategoryDirection) => {
    setCategoryConfigWorkingCopy(configWorkingCopy =>
      moveCategoryPosition(subCategoryId, moveCategoryDirection, configWorkingCopy)
    );
  };

  const setCategorySearchUrl = debounce((subCategoryId, searchUrl) => {
    setCategoryConfigWorkingCopy(configWorkingCopy =>
      updateCategoryProperty(subCategoryId, EDITABLE_CATEGORY_PROPERTY.SEARCH_URL, searchUrl, configWorkingCopy)
    );
  }, DEBOUNCE_DURATION_MS);

  const setCategoryTitle = debounce((subCategoryId, title) => {
    setCategoryConfigWorkingCopy(configWorkingCopy =>
      updateCategoryProperty(subCategoryId, EDITABLE_CATEGORY_PROPERTY.TITLE, title, configWorkingCopy)
    );
  }, DEBOUNCE_DURATION_MS);

  const toggleIsEnabled = subCategoryId => {
    setCategoryConfigWorkingCopy(configWorkingCopy => toggleCategoryIsEnabled(subCategoryId, configWorkingCopy));
  };

  const topLevelCategory = categoryConfigWorkingCopy.categoryIndex[topLevelCategoryId];
  const topLevelSubCategories = getSubCategories(topLevelCategoryId, categoryConfigWorkingCopy);
  const hasSubCategories = Boolean(topLevelSubCategories.length);
  const isNew = categoryConfig.updatedAt === 0;

  return (
    <>
      <section style={{ marginBottom: '40px' }}>
        <button
          disabled={!isDirty}
          onClick={() => save(categoryConfigWorkingCopy)}
          style={{ marginBottom: '20px' }}
          type="button"
        >
          {isNew ? 'Create' : 'Update'}
        </button>

        <h3>Step 1: {topLevelCategory.title} browse categories</h3>
        {isNew && <p>Time to create this category config.</p>}

        <CategoryListEditor
          categories={topLevelSubCategories}
          deleteCategory={deleteCategory}
          moveCategory={moveCategory}
          onAddCategoryClick={addCategory}
          parentCategoryId={topLevelCategory.categoryId}
          setCategorySearchUrl={setCategorySearchUrl}
          setCategoryTitle={setCategoryTitle}
          toggleIsEnabled={toggleIsEnabled}
        />
      </section>

      {hasSubCategories && (
        <section>
          <h3>Step 2: Sub-{topLevelCategory.title} configuration</h3>

          {topLevelSubCategories.map(subCategory => {
            const { categoryId, title } = subCategory;
            const isUnsaved = isUnsavedCategory(subCategory);
            if (isUnsaved) {
              return null;
            }

            return (
              <section key={categoryId} style={{ marginBottom: '20px' }}>
                <h4>{title}</h4>
                <CategoryListEditor
                  categories={getSubCategories(categoryId, categoryConfigWorkingCopy)}
                  deleteCategory={deleteCategory}
                  moveCategory={moveCategory}
                  onAddCategoryClick={addCategory}
                  parentCategoryId={categoryId}
                  setCategorySearchUrl={setCategorySearchUrl}
                  setCategoryTitle={setCategoryTitle}
                  toggleIsEnabled={toggleIsEnabled}
                />
              </section>
            );
          })}
        </section>
      )}
    </>
  );
};

CategoryConfigEditor.propTypes = {
  categoryConfig: categoryConfigShape.isRequired,
  save: PropTypes.func.isRequired,
  topLevelCategoryId: PropTypes.string.isRequired
};

export default CategoryConfigEditor;
